import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CameraHelper, PhotoHelper } from '@/library';
import { PhotoService } from '@/service';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { useToast } from '@/context/ToastContext';
import { usePhotoCache } from '@/context/PhotoCacheContext';
import { ImageMetadata } from '@/api/models';
import { IMAGE_PROVIDER_CONNECTION_ERROR_MESSAGE } from '@/auth/GatewayApiAccessToken';
import { isProductionEnv } from '@/utils';
import DeleteModal from '@/common/DeleteModal';
import PhotoCarousel, { PhotoCarouselItem } from '@/common/PhotoCarousel';
import PhotoDebugErrorModal, { createPhotoDebugError, IPhotoDebugError } from './PhotoDebugErrorModal';
import PhotoStrip from './PhotoStrip';
import { IPhotoSectionPhoto } from './types';
import styles from './PhotoCaptureSection.module.scss';

const emptyImageMetadata: ImageMetadata[] = [];

const getPhotoErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error && error.message === IMAGE_PROVIDER_CONNECTION_ERROR_MESSAGE) {
    return IMAGE_PROVIDER_CONNECTION_ERROR_MESSAGE;
  }

  return fallbackMessage;
};

interface IPhotoCaptureSectionProps {
  playerId: number;
  ownerRowGuid: string;
  headTypeId?: number;
  imageMetadata?: ImageMetadata[] | null;
  isASetClub?: boolean;
  isBsg?: boolean;
}

function PhotoCaptureSection({ playerId, ownerRowGuid, headTypeId, imageMetadata, isASetClub, isBsg }: IPhotoCaptureSectionProps) {
  const isOnline = useOnlineStatus();
  const { showToast } = useToast();
  const { getPhotoObjectUrl, getPhotoSectionState, setPhotoSectionDeletedStorageObjectIds, setPhotoSectionEditedPhotoCaptions, setPhotoSectionLocalPhotos } = usePhotoCache();
  const failedFetchesRef = useRef(new Set<string>());
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const [hydratedPhotos, setHydratedPhotos] = useState<IPhotoSectionPhoto[]>([]);
  const [activePhotoId, setActivePhotoId] = useState<string | number>();
  const [pageIndex, setPageIndex] = useState(0);
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState<PhotoCarouselItem>();
  const [debugError, setDebugError] = useState<IPhotoDebugError>();
  const photoSectionState = getPhotoSectionState(playerId, ownerRowGuid);
  const localPhotos = photoSectionState.localPhotos as IPhotoSectionPhoto[];
  const editedPhotoCaptions = photoSectionState.editedPhotoCaptions;
  const deletedStorageObjectIds = photoSectionState.deletedStorageObjectIds;
  const ownerImageMetadata = useMemo(() => imageMetadata ?? emptyImageMetadata, [imageMetadata]);
  const displayPhotos = PhotoHelper.mergeHydratedPhotosWithLocal(hydratedPhotos, localPhotos);
  const visiblePhotos = PhotoHelper.getVisiblePhotoItems(displayPhotos, pageIndex);
  const showPhotoStrip = visiblePhotos.length > 0;

  const showDebugError = (action: string, error: unknown) => {
    if (isProductionEnv) return;
    setDebugError(createPhotoDebugError(action, error));
  };

  const setLocalPhotos = useCallback(
    (updater: SetStateAction<IPhotoSectionPhoto[]>) => setPhotoSectionLocalPhotos(playerId, ownerRowGuid, updater),
    [ownerRowGuid, playerId, setPhotoSectionLocalPhotos],
  );

  const setDeletedStorageObjectIds = useCallback(
    (updater: SetStateAction<Set<number>>) => setPhotoSectionDeletedStorageObjectIds(playerId, ownerRowGuid, updater),
    [ownerRowGuid, playerId, setPhotoSectionDeletedStorageObjectIds],
  );

  const setEditedPhotoCaptions = useCallback(
    (updater: SetStateAction<Record<number, string>>) => setPhotoSectionEditedPhotoCaptions(playerId, ownerRowGuid, updater),
    [ownerRowGuid, playerId, setPhotoSectionEditedPhotoCaptions],
  );

  useEffect(() => {
    failedFetchesRef.current.clear();
  }, [ownerRowGuid, playerId]);

  useEffect(() => {
    let isCancelled = false;

    const hydratePhotos = async () => {
      if (!isOnline || !ownerImageMetadata.length) {
        setHydratedPhotos([]);
        setIsFetchingImages(false);
        return;
      }

      const nextPhotos: IPhotoSectionPhoto[] = [];
      setIsFetchingImages(true);

      for (const metadata of ownerImageMetadata) {
        if (!metadata.fileName) continue;
        if (deletedStorageObjectIds.has(metadata.storageObjectId)) continue;

        const fetchKey = `${metadata.storageObjectId}:${metadata.fileName}`;
        if (failedFetchesRef.current.has(fetchKey)) continue;

        try {
          const src = await getPhotoObjectUrl(playerId, ownerRowGuid, metadata.fileName);
          const editedCaption = editedPhotoCaptions[metadata.storageObjectId];
          const nextMetadata = editedCaption === undefined ? metadata : { ...metadata, caption: editedCaption };
          nextPhotos.push(PhotoHelper.createPhotoFromMetadata(nextMetadata, src));
        } catch (error) {
          console.error('Failed to fetch photo:', error);
          showDebugError(`Fetch image: ${metadata.fileName}`, error);
          failedFetchesRef.current.add(fetchKey);
          showToast({ message: getPhotoErrorMessage(error, 'Photo failed to load.'), status: 'error' });
        }
      }

      if (!isCancelled) {
        setHydratedPhotos(nextPhotos);
        setIsFetchingImages(false);
      }
    };

    hydratePhotos();

    return () => {
      isCancelled = true;
    };
  }, [deletedStorageObjectIds, editedPhotoCaptions, getPhotoObjectUrl, isOnline, ownerImageMetadata, ownerRowGuid, playerId, showToast]);

  useEffect(() => {
    setPageIndex((currentPageIndex) => Math.min(currentPageIndex, PhotoHelper.getMaxPhotoPageIndex(displayPhotos.length)));
  }, [displayPhotos.length]);

  const createPhotoFromBlob = (photo: Blob, index: number, uploadTime: number) => {
    const src = URL.createObjectURL(photo);
    return PhotoHelper.createLocalPhoto({ photo, index, uploadTime, src });
  };

  const uploadPhoto = async (photo: IPhotoSectionPhoto) => {
    if (!photo.sourceBlob) return;

    setLocalPhotos((currentPhotos) => currentPhotos.map((currentPhoto) => (currentPhoto.id === photo.id ? { ...currentPhoto, uploadStatus: 'processing', uploadError: undefined } : currentPhoto)));

    try {
      const uploadResult = await PhotoService.uploadPhoto({
        playerId,
        ownerRowGuid,
        headTypeId,
        photoId: String(photo.id),
        photo: photo.sourceBlob,
        fileName: photo.name,
        caption: photo.caption,
        isASetClub,
        isBsg,
      });

      setLocalPhotos((currentPhotos) =>
        currentPhotos.map((currentPhoto) =>
          currentPhoto.id === photo.id
            ? {
                ...currentPhoto,
                uploadStatus: 'complete',
                fileName: uploadResult.fileName,
                contentType: uploadResult.contentType,
                storageObjectId: uploadResult.metadata?.storageObjectId,
                entityGuid: uploadResult.metadata?.entityGuid,
                metadataRowGuid: uploadResult.metadata?.rowGuid,
                sortId: uploadResult.metadata?.sortId,
                sourceBlob: undefined,
              }
            : currentPhoto,
        ),
      );
    } catch (error) {
      console.error('Failed to upload photo:', error);
      showDebugError(`Upload image: ${photo.name}`, error);
      setLocalPhotos((currentPhotos) =>
        currentPhotos.map((currentPhoto) =>
          currentPhoto.id === photo.id ? { ...currentPhoto, uploadStatus: 'error', uploadError: 'Upload failed. Please try adding the photo again.' } : currentPhoto,
        ),
      );
      showToast({ message: getPhotoErrorMessage(error, 'Photo upload failed. Please try adding the photo again.'), status: 'error' });
    }
  };

  const handleAddPhoto = async () => {
    const capturedPhoto = await CameraHelper.openCamera();
    if (!capturedPhoto) {
      return;
    }

    const nextPhoto = createPhotoFromBlob(capturedPhoto, 0, Date.now());
    setLocalPhotos((currentPhotos) => [...currentPhotos, nextPhoto]);
    uploadPhoto(nextPhoto);
    setPageIndex((currentPageIndex) => PhotoHelper.getPhotoPageIndexAfterAdd(currentPageIndex));
  };

  const openCarousel = (photo: IPhotoSectionPhoto) => {
    setActivePhotoId(photo.id);
    setIsCarouselOpen(true);
  };

  const handleEditPhoto = async (photoToEdit: PhotoCarouselItem, caption: string) => {
    if (!photoToEdit.storageObjectId) {
      showToast({ message: 'Photo caption cannot be saved yet.', status: 'error' });
      return;
    }

    try {
      await PhotoService.updatePhotoCaption({ storageObjectId: photoToEdit.storageObjectId, caption });
      setEditedPhotoCaptions((currentCaptions) => ({ ...currentCaptions, [photoToEdit.storageObjectId!]: caption }));
      setHydratedPhotos((currentPhotos) => PhotoHelper.editPhotoCaption(currentPhotos, photoToEdit.id, caption));
      setLocalPhotos((currentPhotos) => PhotoHelper.editPhotoCaption(currentPhotos, photoToEdit.id, caption));
    } catch (error) {
      console.error('Failed to update photo caption:', error);
      showToast({ message: 'Photo caption failed to save.', status: 'error' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingPhoto) return;
    if (!deletingPhoto.storageObjectId) {
      showToast({ message: 'Photo cannot be deleted yet.', status: 'error' });
      return;
    }

    try {
      await PhotoService.deletePhotoMetadata({ storageObjectId: deletingPhoto.storageObjectId });
      const deleteResult = PhotoHelper.deletePhoto(displayPhotos, deletingPhoto.id, deletingPhoto.id);
      if (localPhotos.some((photo) => photo.src === deletingPhoto.src)) {
        URL.revokeObjectURL(deletingPhoto.src);
      }
      setDeletedStorageObjectIds((currentIds) => new Set(currentIds).add(deletingPhoto.storageObjectId!));
      setHydratedPhotos((currentPhotos) => currentPhotos.filter((photo) => photo.id !== deletingPhoto.id));
      setLocalPhotos((currentPhotos) => currentPhotos.filter((photo) => photo.id !== deletingPhoto.id));
      setDeletingPhoto(undefined);

      if (!deleteResult.photos.length) {
        setIsCarouselOpen(false);
        setActivePhotoId(undefined);
        return;
      }

      setActivePhotoId(deleteResult.activePhotoId);
    } catch (error) {
      console.error('Failed to delete photo:', error);
      showToast({ message: 'Photo failed to delete.', status: 'error' });
    }
  };

  if (!isOnline) return null;

  return (
    <div className={styles.photoSection}>
      <div className={styles.category}>
        <div className={styles.categoryName}>
          Photos{' '}
          <button type="button" className={styles.photoAction} onClick={handleAddPhoto} aria-label="Add Image">
            + Add Image
          </button>
          {isFetchingImages && (
            <span className={styles.imageLoadingIndicator}>
              <span className={styles.imageLoadingSpinner} aria-hidden="true" />
              Loading images
            </span>
          )}
        </div>
      </div>

      {showPhotoStrip && (
        <PhotoStrip
          photos={visiblePhotos}
          pageIndex={pageIndex}
          pageSize={PhotoHelper.PHOTO_PAGE_SIZE}
          totalItems={displayPhotos.length}
          onNextPage={() => setPageIndex((currentPageIndex) => PhotoHelper.getNextPhotoPageIndex(displayPhotos.length, currentPageIndex))}
          onPreviousPage={() => setPageIndex(PhotoHelper.getPreviousPhotoPageIndex)}
          onPhotoClick={openCarousel}
        />
      )}

      <PhotoCarousel isOpen={isCarouselOpen} photos={displayPhotos} initialPhotoId={activePhotoId} onClose={() => setIsCarouselOpen(false)} onEdit={handleEditPhoto} onDelete={setDeletingPhoto} />

      {deletingPhoto && (
        <DeleteModal title="Delete photo?" body="This photo will be removed from the local list." handleCancel={() => setDeletingPhoto(undefined)} handleConfirm={handleConfirmDelete} />
      )}

      {!isProductionEnv && <PhotoDebugErrorModal error={debugError} onClose={() => setDebugError(undefined)} />}
    </div>
  );
}

export default PhotoCaptureSection;
